import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { emailService } from '../services/emailService';
import {
    Box, Container, Typography, Button, Card, CardContent,
    Grid, Fade, CircularProgress, useTheme, alpha, Stack,
    Paper, Divider, IconButton, Badge
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SecurityIcon from '@mui/icons-material/Security';
import TimerIcon from '@mui/icons-material/Timer';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import SpeedIcon from '@mui/icons-material/Speed';
import ShieldIcon from '@mui/icons-material/Shield';
import DevicesIcon from '@mui/icons-material/Devices';
import StorageIcon from '@mui/icons-material/Storage';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import NotificationsIcon from '@mui/icons-material/Notifications';
import GoogleAd from './GoogleAd';

// Constants
const DOMAIN = 'wmart.in';

// Email storage key
const EMAIL_STORAGE_KEY = 'current_temp_email';

// Ad placeholder component
const AdPlaceholder = ({ width, height, label, position = 'relative' }: { width: string, height: string, label: string, position?: string }) => {
    const theme = useTheme();
    const isLightMode = theme.palette.mode === 'light';

    return (
        <Paper
            elevation={0}
            sx={{
                width,
                height,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isLightMode ? 'rgba(0, 0, 0, 0.03)' : 'rgba(255, 255, 255, 0.05)',
                borderRadius: 1,
                border: '1px dashed',
                borderColor: isLightMode ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                color: isLightMode ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.4)',
                fontSize: '0.8rem',
                fontWeight: 500,
                margin: '8px 0',
                position,
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                '&:hover': {
                    bgcolor: isLightMode ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.08)',
                }
            }}
        >
            {label}
        </Paper>
    );
};

// Feature items with their icons and descriptions
const features = [
    {
        icon: <SecurityIcon />,
        title: 'Secure & Anonymous',
        description: 'Keep your real email private and avoid spam. Our service ensures your personal information stays protected.'
    },
    {
        icon: <TimerIcon />,
        title: 'Temporary',
        description: 'Emails automatically expire after 24 hours. No maintenance needed, we handle the cleanup.'
    },
    {
        icon: <LockIcon />,
        title: 'No Registration',
        description: 'Generate emails instantly without any signup process. No personal information required.'
    },
    {
        icon: <PublicIcon />,
        title: 'Private Domain',
        description: 'Use our secure custom domain for enhanced privacy. All emails are encrypted end-to-end.'
    }
];

const additionalFeatures = [
    {
        icon: <SpeedIcon />,
        title: 'Instant Access',
        description: 'Get your temporary email address in seconds. No waiting, no verification needed.'
    },
    {
        icon: <ShieldIcon />,
        title: 'Spam Protection',
        description: 'Advanced filters block unwanted emails and protect you from phishing attempts.'
    },
    {
        icon: <DevicesIcon />,
        title: 'Mobile Friendly',
        description: 'Access your temporary inbox from any device with our responsive design.'
    },
    {
        icon: <StorageIcon />,
        title: 'File Attachments',
        description: 'Receive and download file attachments securely from your temporary inbox.'
    }
];

interface EmailBoxProps {
    email: string;
    onCopy: () => void;
    copied: boolean;
    onCheck: () => void;
    onDelete: () => void;
    onGenerateNew: () => void;
    previousEmails?: string[];
    onViewPrevious?: (email: string) => void;
    onDeletePrevious?: (email: string) => void;
    hasNewEmails?: boolean;
    checkingEmails?: boolean;
}

const EmailBox = ({
    email,
    onCopy,
    copied,
    onCheck,
    onDelete,
    onGenerateNew,
    previousEmails = [],
    onDeletePrevious,
    hasNewEmails = false,
    checkingEmails = false
}: EmailBoxProps) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const [animateEmail, setAnimateEmail] = useState(false);

    // Play animation when email changes
    useEffect(() => {
        if (email) {
            setAnimateEmail(true);
            const timer = setTimeout(() => setAnimateEmail(false), 1500);
            return () => clearTimeout(timer);
        }
    }, [email]);

    // Animation handlers for buttons
    const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        const button = event.currentTarget;

        // Add and remove animation class
        button.classList.add('button-click-effect');
        setTimeout(() => {
            button.classList.remove('button-click-effect');
        }, 300);
    };

    return (
        <Box sx={{ textAlign: 'center' }}>
            <Typography
                variant="h6"
                gutterBottom
                sx={{
                    fontWeight: 600,
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)',
                    mb: 2
                }}
            >
                Your Temporary Email
            </Typography>
            <Box
                sx={{
                    position: 'relative',
                    mb: 2,
                    p: 0.5,
                    borderRadius: 2,
                    background: isDarkMode ? '#333333' : '#f0f0f0',
                    overflow: 'hidden',
                }}
            >
                <Typography
                    variant="h5"
                    className={animateEmail ? 'email-change-animation' : ''}
                    sx={{
                        fontFamily: 'monospace',
                        p: 2.5,
                        fontWeight: 600,
                        letterSpacing: '0.02em',
                        position: 'relative',
                        overflow: 'hidden',
                        wordBreak: 'break-all',
                        textAlign: 'center',
                        color: isDarkMode ? '#FFFFFF' : 'inherit',
                        '&.email-change-animation': {
                            animation: 'emailChangeEffect 1.5s ease-in-out'
                        }
                    }}
                >
                    {email}
                </Typography>
            </Box>
            <Stack
                spacing={2}
                sx={{ mt: 2 }}
            >
                <Stack
                    direction="row"
                    spacing={2}
                >
                    <Button
                        variant="outlined"
                        onClick={(e) => {
                            handleButtonClick(e);
                            onCopy();
                        }}
                        fullWidth
                        sx={{
                            py: 1.5,
                            fontWeight: 600,
                            bgcolor: isDarkMode ? '#333333' : '#ffffff',
                            borderColor: isDarkMode ? '#555555' : '#dddddd',
                            color: isDarkMode ? '#FFFFFF' : '#000000',
                            borderRadius: 1,
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                                bgcolor: isDarkMode ? '#444444' : '#f8f8f8',
                                borderColor: isDarkMode ? '#666666' : '#cccccc',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                            },
                            '&:active': {
                                transform: 'translateY(1px)',
                                boxShadow: 'none'
                            },
                            '&.button-click-effect': {
                                animation: 'button-pulse 0.3s ease-out'
                            }
                        }}
                    >
                        {copied ? 'Copied!' : 'Copy Email'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={(e) => {
                            handleButtonClick(e);
                            onCheck();
                        }}
                        fullWidth
                        sx={{
                            py: 1.5,
                            fontWeight: 600,
                            bgcolor: isDarkMode ? '#444444' : '#000000',
                            color: '#FFFFFF',
                            borderRadius: 1,
                            transition: 'all 0.2s ease-in-out',
                            position: 'relative',
                            overflow: 'visible',
                            '&:hover': {
                                bgcolor: isDarkMode ? '#555555' : '#111111',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                            },
                            '&:active': {
                                transform: 'translateY(1px)',
                                boxShadow: 'none'
                            },
                            '&.button-click-effect': {
                                animation: 'button-pulse 0.3s ease-out'
                            },
                            '&.new-email-pulse': {
                                animation: 'emailPulse 2s infinite'
                            }
                        }}
                        className={hasNewEmails ? 'new-email-pulse' : ''}
                    >
                        {checkingEmails ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <CircularProgress size={16} sx={{ color: 'white', mr: 1 }} />
                                Checking...
                            </Box>
                        ) : (
                            <>
                                Check Inbox
                                {hasNewEmails && (
                                    <Badge
                                        color="error"
                                        variant="dot"
                                        sx={{
                                            position: 'absolute',
                                            top: 8,
                                            right: 8,
                                            '& .MuiBadge-badge': {
                                                animation: 'badgePulse 1.5s infinite',
                                                transformOrigin: 'center',
                                                boxShadow: '0 0 0 rgba(255, 255, 255, 0.4)'
                                            }
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </Button>
                </Stack>
                <Button
                    variant="contained"
                    onClick={(e) => {
                        handleButtonClick(e);
                        onGenerateNew();
                    }}
                    fullWidth
                    sx={{
                        py: 1.5,
                        fontWeight: 600,
                        bgcolor: isDarkMode ? '#444444' : '#000000',
                        color: '#FFFFFF',
                        borderRadius: 1,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            bgcolor: isDarkMode ? '#555555' : '#111111',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                        },
                        '&:active': {
                            transform: 'translateY(1px)',
                            boxShadow: 'none'
                        },
                        '&.button-click-effect': {
                            animation: 'button-pulse 0.3s ease-out'
                        }
                    }}
                >
                    Generate New Email
                </Button>
                <Button
                    variant="contained"
                    onClick={(e) => {
                        handleButtonClick(e);
                        onDelete();
                    }}
                    fullWidth
                    sx={{
                        py: 1.5,
                        fontWeight: 600,
                        bgcolor: isDarkMode ? '#444444' : '#000000',
                        color: '#FFFFFF',
                        borderRadius: 1,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            bgcolor: isDarkMode ? '#555555' : '#111111',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                        },
                        '&:active': {
                            transform: 'translateY(1px)',
                            boxShadow: 'none'
                        },
                        '&.button-click-effect': {
                            animation: 'button-pulse 0.3s ease-out'
                        }
                    }}
                >
                    Delete All and Change Email
                </Button>

                {previousEmails.length > 0 && (
                    <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                mb: 1.5,
                                fontWeight: 600,
                                color: isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)'
                            }}
                        >
                            Previous Emails
                        </Typography>
                        <Stack spacing={1}>
                            {previousEmails.map((prevEmail, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%'
                                    }}
                                >
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={(e) => {
                                            handleButtonClick(e);
                                            window.open(`/inbox/${prevEmail}`, '_blank');
                                        }}
                                        sx={{
                                            py: 0.75,
                                            flex: 1,
                                            fontFamily: 'monospace',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                                            borderColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                            color: isDarkMode ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.75)',
                                            textTransform: 'none',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            textAlign: 'left',
                                            justifyContent: 'flex-start',
                                            transition: 'all 0.2s ease-in-out',
                                            '&:hover': {
                                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                                borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                                                transform: 'translateY(-1px)',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                            },
                                            '&:active': {
                                                transform: 'translateY(1px)',
                                                boxShadow: 'none'
                                            },
                                            '&.button-click-effect': {
                                                animation: 'button-pulse 0.3s ease-out'
                                            }
                                        }}
                                    >
                                        {prevEmail}
                                    </Button>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            const btn = e.currentTarget;
                                            btn.classList.add('icon-click-effect');
                                            setTimeout(() => btn.classList.remove('icon-click-effect'), 300);
                                            onDeletePrevious && onDeletePrevious(prevEmail);
                                        }}
                                        sx={{
                                            ml: 1,
                                            color: isDarkMode ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                                            transition: 'all 0.2s ease-in-out',
                                            '&:hover': {
                                                color: isDarkMode ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)',
                                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                                transform: 'scale(1.1)'
                                            },
                                            '&:active': {
                                                transform: 'scale(0.95)'
                                            },
                                            '&.icon-click-effect': {
                                                animation: 'icon-pulse 0.3s ease-out'
                                            }
                                        }}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                )}
            </Stack>
        </Box>
    );
};

const HomePage = () => {
    const [email, setEmail] = useState(() => {
        // Initialize email from localStorage if available
        return localStorage.getItem(EMAIL_STORAGE_KEY) || '';
    });
    const [previousEmails, setPreviousEmails] = useState<string[]>(() => {
        // Initialize previous emails from localStorage if available
        const storedEmails = localStorage.getItem('previous_emails');
        return storedEmails ? JSON.parse(storedEmails) : [];
    });
    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(false);
    const generateEmailRequest = useRef<AbortController | null>(null);
    const navigate = useNavigate();
    const theme = useTheme();
    const [hasNewEmails, setHasNewEmails] = useState(false);
    const [checkingEmails, setCheckingEmails] = useState(false);
    const checkEmailsInterval = useRef<NodeJS.Timeout | null>(null);

    // Filter out emails older than 7 days
    useEffect(() => {
        const cleanupOldEmails = () => {
            // Get all email timestamps
            const emailTimestamps = localStorage.getItem('email_timestamps');
            if (emailTimestamps) {
                const timestamps: Record<string, number> = JSON.parse(emailTimestamps);
                const now = Date.now();
                const sevenDaysMs = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

                // Filter out emails older than 7 days
                let hasOldEmails = false;
                const updatedTimestamps: Record<string, number> = {};

                Object.keys(timestamps).forEach(email => {
                    const timestamp = timestamps[email];
                    if (now - timestamp < sevenDaysMs) {
                        // Keep emails newer than 7 days
                        updatedTimestamps[email] = timestamp;
                    } else {
                        hasOldEmails = true;
                    }
                });

                // If we found any old emails, update localStorage
                if (hasOldEmails) {
                    // Update timestamps storage
                    localStorage.setItem('email_timestamps', JSON.stringify(updatedTimestamps));

                    // Update previous emails list
                    const storedEmails = localStorage.getItem('previous_emails');
                    if (storedEmails) {
                        const emails: string[] = JSON.parse(storedEmails);
                        const updatedEmails = emails.filter(email =>
                            Object.keys(updatedTimestamps).includes(email)
                        );
                        localStorage.setItem('previous_emails', JSON.stringify(updatedEmails));
                        setPreviousEmails(updatedEmails);
                    }

                    // If current email is too old, clear it
                    const currentEmail = localStorage.getItem(EMAIL_STORAGE_KEY);
                    if (currentEmail && !Object.keys(updatedTimestamps).includes(currentEmail)) {
                        localStorage.removeItem(EMAIL_STORAGE_KEY);
                        setEmail('');
                    }
                }
            }
        };

        // Run cleanup on component mount
        cleanupOldEmails();

        // Set interval to run cleanup every hour
        const interval = setInterval(cleanupOldEmails, 60 * 60 * 1000);

        return () => clearInterval(interval);
    }, []);

    // Persist email to localStorage whenever it changes
    useEffect(() => {
        if (email) {
            localStorage.setItem(EMAIL_STORAGE_KEY, email);

            // Also record the timestamp for when this email was created/updated
            const emailTimestamps = localStorage.getItem('email_timestamps') || '{}';
            const timestamps = JSON.parse(emailTimestamps);
            timestamps[email] = Date.now();
            localStorage.setItem('email_timestamps', JSON.stringify(timestamps));
        }
    }, [email]);

    // Persist previous emails to localStorage whenever they change
    useEffect(() => {
        if (previousEmails.length > 0) {
            localStorage.setItem('previous_emails', JSON.stringify(previousEmails));
        }
    }, [previousEmails]);

    // Clean up any pending requests when component unmounts
    useEffect(() => {
        return () => {
            if (generateEmailRequest.current) {
                generateEmailRequest.current.abort();
            }
        };
    }, []);

    // Check for new emails periodically
    useEffect(() => {
        if (email) {
            // Set up interval to check for new emails every 30 seconds
            const checkForNewEmails = async () => {
                if (!email) return;

                try {
                    // Simulate checking for new emails
                    setCheckingEmails(true);

                    // This is where you would normally call your API
                    // For demo purposes, we'll randomly indicate new emails sometimes
                    await new Promise(resolve => setTimeout(resolve, 1000));

                    // Random chance of new emails (25%)
                    if (Math.random() < 0.25) {
                        setHasNewEmails(true);
                    }
                } catch (error) {
                    console.error('Error checking for new emails:', error);
                } finally {
                    setCheckingEmails(false);
                }
            };

            // Check immediately on mount
            checkForNewEmails();

            // Set up the interval
            checkEmailsInterval.current = setInterval(checkForNewEmails, 30000);

            return () => {
                if (checkEmailsInterval.current) {
                    clearInterval(checkEmailsInterval.current);
                }
            };
        }
    }, [email]);

    const handleGenerateEmail = async () => {
        try {
            if (loading) return;

            // Cancel any existing request
            if (generateEmailRequest.current) {
                generateEmailRequest.current.abort();
            }

            // Create a new controller for this request
            generateEmailRequest.current = new AbortController();

            setLoading(true);

            // Use the email service to generate a real email with the abort signal
            const result = await emailService.generateEmail(generateEmailRequest.current.signal);

            // Apply the new email with animation effect
            setEmail(result.email);
        } catch (error) {
            // Only log errors that aren't from aborting
            if (!(error instanceof DOMException && error.name === 'AbortError')) {
                console.error('Error generating email:', error);
            }
        } finally {
            setLoading(false);
            generateEmailRequest.current = null;
        }
    };

    const handleGenerateNewEmail = async () => {
        // If we have a current email, add it to previous emails before generating a new one
        if (email) {
            // Add to previous emails if it's not already there
            if (!previousEmails.includes(email)) {
                // Keep only the last 5 previous emails
                const updatedPreviousEmails = [email, ...previousEmails].slice(0, 5);
                setPreviousEmails(updatedPreviousEmails);

                // Also record the timestamp for when this email was added to history
                const emailTimestamps = localStorage.getItem('email_timestamps') || '{}';
                const timestamps = JSON.parse(emailTimestamps);
                if (!timestamps[email]) {
                    timestamps[email] = Date.now();
                    localStorage.setItem('email_timestamps', JSON.stringify(timestamps));
                }
            }
        }

        // Generate a new email
        await handleGenerateEmail();
    };

    const handleCopyEmail = async () => {
        if (email) {
            try {
                await navigator.clipboard.writeText(email);
                setCopied(true);
                // Reset the copied state after 2 seconds
                setTimeout(() => setCopied(false), 2000);
            } catch (error) {
                console.error('Failed to copy email to clipboard:', error);
                // Fallback method using temporary input element
                const tempInput = document.createElement('input');
                tempInput.value = email;
                document.body.appendChild(tempInput);
                tempInput.select();
                document.execCommand('copy');
                document.body.removeChild(tempInput);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }
        }
    };

    const handleCheckEmails = () => {
        if (email && email.trim() !== '') {
            // Save the current email again just to be sure
            localStorage.setItem(EMAIL_STORAGE_KEY, email);

            // Reset new emails flag when checking inbox
            setHasNewEmails(false);

            // Navigate to inbox
            navigate(`/inbox/${email}`);
        } else {
            // If no email is available, alert the user
            alert('Please generate an email first before checking the inbox.');
        }
    };

    const handleDeleteEmail = () => {
        // Remove email timestamp
        if (email) {
            const emailTimestamps = localStorage.getItem('email_timestamps') || '{}';
            const timestamps = JSON.parse(emailTimestamps);
            delete timestamps[email];
            localStorage.setItem('email_timestamps', JSON.stringify(timestamps));
        }

        // Clear the email from state and storage
        setEmail('');
        localStorage.removeItem(EMAIL_STORAGE_KEY);

        // Clear previous emails
        setPreviousEmails([]);
        localStorage.removeItem('previous_emails');

        // Generate a new email
        handleGenerateEmail();
    };

    const handleDeletePreviousEmail = (emailToDelete: string) => {
        // Remove the email from the previousEmails array
        const updatedEmails = previousEmails.filter(email => email !== emailToDelete);
        setPreviousEmails(updatedEmails);

        // Update localStorage
        localStorage.setItem('previous_emails', JSON.stringify(updatedEmails));

        // Remove the timestamp for this email
        const emailTimestamps = localStorage.getItem('email_timestamps') || '{}';
        const timestamps = JSON.parse(emailTimestamps);
        delete timestamps[emailToDelete];
        localStorage.setItem('email_timestamps', JSON.stringify(timestamps));
    };

    // Add global keyframes for button animations using useEffect
    useEffect(() => {
        // Create style element for animations
        const styleEl = document.createElement('style');
        styleEl.innerHTML = `
            @keyframes button-pulse {
                0% { transform: scale(1); }
                50% { transform: scale(0.97); }
                100% { transform: scale(1); }
            }
            
            @keyframes button-pulse-large {
                0% { transform: scale(1); }
                10% { transform: scale(0.95); }
                50% { transform: scale(1.03); }
                100% { transform: scale(1); }
            }
            
            @keyframes icon-pulse {
                0% { transform: scale(1); }
                50% { transform: scale(0.8); }
                100% { transform: scale(1); }
            }
            
            @keyframes badgePulse {
                0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.7); }
                70% { transform: scale(1.2); box-shadow: 0 0 0 5px rgba(244, 67, 54, 0); }
                100% { transform: scale(1); }
            }
            
            @keyframes emailPulse {
                0% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.4); }
                70% { box-shadow: 0 0 0 8px rgba(244, 67, 54, 0); }
                100% { box-shadow: 0 0 0 0 rgba(244, 67, 54, 0); }
            }
            
            @keyframes emailChangeEffect {
                0% { 
                    opacity: 0;
                    transform: translateY(10px) scale(0.95);
                    filter: blur(8px);
                }
                20% {
                    opacity: 0.6;
                    transform: translateY(5px) scale(0.97);
                    filter: blur(4px);
                }
                60% {
                    opacity: 1;
                    transform: translateY(0) scale(1.03);
                    filter: blur(0);
                    text-shadow: 0 0 8px rgba(0, 0, 0, 0.3);
                }
                100% { 
                    opacity: 1;
                    transform: translateY(0) scale(1);
                    filter: blur(0);
                    text-shadow: none;
                }
            }
            
            @keyframes typingEffect {
                from { width: 0 }
                to { width: 100% }
            }
        `;
        document.head.appendChild(styleEl);

        // Clean up
        return () => {
            document.head.removeChild(styleEl);
        };
    }, []);

    return (
        <main>
            <Box component="section" aria-labelledby="hero-heading" sx={{ pt: { xs: 4, sm: 6, md: 8 }, pb: { xs: 6, sm: 8, md: 10 } }}>
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={7} lg={6}>
                            <Box sx={{ textAlign: { xs: 'center', md: 'left' }, mb: { xs: 4, md: 0 } }}>
                                <Typography
                                    component="h1"
                                    variant="h2"
                                    id="hero-heading"
                                    sx={{
                                        fontWeight: 800,
                                        fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3rem' },
                                        mb: 2,
                                        background: theme => theme.palette.mode === 'dark'
                                            ? 'linear-gradient(to right, #FFFFFF, #E0E0E0)'
                                            : 'linear-gradient(to right, #000000, #333333)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent'
                                    }}
                                >
                                    Temporary Email for Enhanced Online Privacy
                                </Typography>

                                <Typography
                                    variant="h6"
                                    component="h2"
                                    gutterBottom
                                    sx={{
                                        fontWeight: 400,
                                        lineHeight: 1.5,
                                        mb: 3,
                                        color: 'text.secondary'
                                    }}
                                >
                                    Create disposable email addresses instantly with no registration. Protect your real inbox from spam and safeguard your privacy online.
                                </Typography>

                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: { xs: 'center', md: 'flex-start' }, mb: 4 }}>
                                    {['Free', 'No Sign-up', 'Anonymous', 'Secure', '7-day Access'].map((tag, index) => (
                                        <Box
                                            key={index}
                                            component="span"
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: '16px',
                                                fontSize: '0.875rem',
                                                fontWeight: 600,
                                                bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)',
                                                color: 'text.primary',
                                            }}
                                        >
                                            <CheckCircleIcon fontSize="small" sx={{ mr: 0.5, fontSize: '1rem' }} />
                                            {tag}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={5} lg={6}>
                            <Card
                                elevation={0}
                                sx={{
                                    overflow: 'hidden',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 3,
                                    position: 'relative',
                                    bgcolor: theme.palette.mode === 'dark' ? '#1a1a1a' : 'background.paper',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: -1,
                                        left: 0,
                                        right: 0,
                                        height: '4px',
                                        background: theme => theme.palette.mode === 'dark'
                                            ? 'linear-gradient(to right, #FFFFFF, #E0E0E0)'
                                            : 'linear-gradient(to right, #000000, #333333)',
                                    }
                                }}
                            >
                                <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                                    {!email ? (
                                        <Box sx={{
                                            textAlign: 'center',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    mb: 1.5,
                                                    fontWeight: 700,
                                                    textAlign: 'center',
                                                    color: theme => theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'
                                                }}
                                            >
                                                Quick, Easy, and Secure.
                                            </Typography>
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                onClick={(e) => {
                                                    const btn = e.currentTarget;
                                                    btn.classList.add('button-pulse-effect');
                                                    setTimeout(() => btn.classList.remove('button-pulse-effect'), 600);
                                                    handleGenerateEmail();
                                                }}
                                                disabled={loading}
                                                startIcon={loading ?
                                                    <CircularProgress size={20} sx={{ color: 'inherit' }} /> :
                                                    <EmailIcon sx={{
                                                        fontSize: 24,
                                                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                                                    }} />
                                                }
                                                sx={{
                                                    py: 2.5,
                                                    maxWidth: '100%',
                                                    width: '100%',
                                                    fontSize: '1.25rem',
                                                    fontWeight: 700,
                                                    letterSpacing: '0.5px',
                                                    textTransform: 'none',
                                                    borderRadius: 1,
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    bgcolor: theme.palette.mode === 'dark' ? '#000000' : '#000000',
                                                    color: '#FFFFFF',
                                                    border: 'none',
                                                    transition: 'all 0.25s ease-in-out',
                                                    '&:hover': {
                                                        bgcolor: theme.palette.mode === 'dark' ? '#222222' : '#111111',
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: '0 6px 12px rgba(0,0,0,0.2)'
                                                    },
                                                    '&:active': {
                                                        transform: 'translateY(1px)',
                                                        boxShadow: 'none'
                                                    },
                                                    '&.button-pulse-effect': {
                                                        animation: 'button-pulse-large 0.6s ease-out'
                                                    },
                                                    '&.Mui-disabled': {
                                                        bgcolor: theme.palette.mode === 'dark'
                                                            ? 'rgba(255,255,255,0.1)'
                                                            : 'rgba(0,0,0,0.1)',
                                                        color: theme.palette.mode === 'dark'
                                                            ? 'rgba(255,255,255,0.3)'
                                                            : 'rgba(0,0,0,0.3)'
                                                    }
                                                }}
                                            >
                                                {loading ? 'Generating...' : 'Generate Email'}
                                            </Button>
                                            <Typography
                                                variant="caption"
                                                component="div"
                                                align="center"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    mt: 2.5,
                                                    opacity: 0.75,
                                                    fontWeight: 500,
                                                    fontSize: '0.8rem',
                                                    color: theme => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                                                    '& svg': {
                                                        fontSize: '0.9rem',
                                                        mr: 0.5
                                                    }
                                                }}
                                            >
                                                <LockIcon fontSize="small" /> No sign-up · 100% Anonymous · SSL Secured
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box>
                                            <EmailBox
                                                email={email}
                                                onCopy={handleCopyEmail}
                                                copied={copied}
                                                onCheck={handleCheckEmails}
                                                onDelete={handleDeleteEmail}
                                                onGenerateNew={handleGenerateNewEmail}
                                                previousEmails={previousEmails}
                                                onDeletePrevious={handleDeletePreviousEmail}
                                                hasNewEmails={hasNewEmails}
                                                checkingEmails={checkingEmails}
                                            />
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* How It Works Section */}
            <Box sx={{ mb: 8 }}>
                <Typography
                    variant="h3"
                    align="center"
                    sx={{
                        mb: 4,
                        fontWeight: 700,
                        fontSize: { xs: '1.5rem', sm: '2rem' }
                    }}
                >
                    How It Works
                </Typography>
                <Grid container spacing={4} alignItems="center">
                    <Grid item xs={12} md={6}>
                        <Box sx={{ p: 2 }}>
                            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                                Simple, Fast, and Secure
                            </Typography>
                            <Typography variant="body1" color="text.secondary" paragraph>
                                1TimeMail provides you with a temporary email address that automatically expires after 24 hours. Perfect for those times when you need a quick email address for signups or testing.
                            </Typography>
                            <Stack spacing={2}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <CheckCircleIcon sx={{ color: theme => theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000' }} />
                                    <Typography>Click 'Generate Email' to get your temporary address</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <CheckCircleIcon sx={{ color: theme => theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000' }} />
                                    <Typography>Use it anywhere you need an email verification</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <CheckCircleIcon sx={{ color: theme => theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000' }} />
                                    <Typography>Access your inbox instantly to view messages</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <CheckCircleIcon sx={{ color: theme => theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000' }} />
                                    <Typography>All data is automatically deleted after 24 hours</Typography>
                                </Box>
                            </Stack>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                p: 4,
                                bgcolor: 'background.paper',
                                borderRadius: 4,
                                border: '1px solid',
                                borderColor: 'divider',
                                boxShadow: theme.shadows[4],
                                overflow: 'hidden'
                            }}
                        >
                            {/* Inbox Preview Placeholder */}
                            <Box sx={{
                                p: 4,
                                textAlign: 'center',
                                color: 'text.secondary',
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                                borderRadius: 2,
                                minHeight: 300,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                gap: 2
                            }}>
                                <EmailIcon sx={{ fontSize: 64, opacity: 0.3 }} />
                                <Typography variant="h6" sx={{ opacity: 0.7 }}>Inbox Preview</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.5 }}>Your emails will appear here</Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* Additional Features */}
            <Box sx={{ mb: 8 }}>
                <Typography
                    variant="h3"
                    align="center"
                    sx={{
                        mb: 4,
                        fontWeight: 700,
                        fontSize: { xs: '1.5rem', sm: '2rem' }
                    }}
                >
                    More Great Features
                </Typography>
                <Grid container spacing={3}>
                    {additionalFeatures.map((feature, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                            <Card
                                elevation={0}
                                sx={{
                                    height: '100%',
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: theme => theme.palette.mode === 'dark'
                                            ? '0 8px 20px rgba(255, 255, 255, 0.1)'
                                            : '0 8px 20px rgba(0, 0, 0, 0.1)',
                                        borderColor: theme => theme.palette.mode === 'dark'
                                            ? 'rgba(255, 255, 255, 0.2)'
                                            : 'rgba(0, 0, 0, 0.2)'
                                    }
                                }}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 56,
                                                height: 56,
                                                p: 1.5,
                                                borderRadius: '50%',
                                                bgcolor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                                                color: theme => theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                                                mr: 2
                                            }}
                                        >
                                            {feature.icon}
                                        </Box>
                                        <Typography variant="h6" fontWeight={600}>
                                            {feature.title}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">
                                        {feature.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* CTA Section */}
            <Box
                sx={{
                    textAlign: 'center',
                    p: 4,
                    borderRadius: 3,
                    bgcolor: theme => theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.05)',
                    border: '1px solid',
                    borderColor: theme => theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.1)'
                        : 'rgba(0, 0, 0, 0.1)'
                }}
            >
                <Typography variant="h4" gutterBottom fontWeight={700}>
                    Ready to protect your inbox?
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
                    Join thousands of users who trust 1TimeMail for their temporary email needs. Generate your disposable email address now and keep your personal information private.
                </Typography>
                <Button
                    variant="contained"
                    size="large"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    sx={{
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '1.1rem'
                    }}
                >
                    Get Started
                </Button>
            </Box>

            {/* Keep one bottom banner ad for monetization */}
            <Box sx={{ mt: 6, mb: 2 }}>
                <GoogleAd
                    format="auto"
                    slot="4680688448"
                    style={{
                        height: '90px',
                        width: '100%',
                        maxWidth: '728px',
                        margin: '0 auto',
                        borderRadius: '8px',
                        overflow: 'hidden'
                    }}
                />
            </Box>
        </main>
    );
};

export default HomePage; 